import Box from '@mui/material/Box';
import { memo } from 'react';

import config from '../../config';
import { useMapsLibLoaded } from '../../util/mapsLib';
import { DynamicMap, StaticMap } from './MapboxMap';

// .root from Map.module.css — applied only when no rootClassName override is passed.
const rootSx = (theme) => ({
  width: '100%',
  height: '100%',
  backgroundColor: theme.tokens.matterColorNegative,
});

// .mapRoot from Map.module.css — applied only when no mapRootClassName override is passed.
const mapRootSx = {
  width: '100%',
  height: '100%',
};

/**
 * @param {Object} props
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @param {string} [props.mapRootClassName]
 * @param {string} [props.address]
 * @param {Object} [props.center]
 * @param {Object} [props.obfuscatedCenter]
 * @param {number} [props.zoom]
 * @param {Object} [props.mapsConfig]
 * @param {boolean} [props.useStaticMap]
 */
const MapComponent = ({
  className = null,
  rootClassName = null,
  mapRootClassName = null,
  address = '',
  center,
  obfuscatedCenter,
  zoom = config.maps.fuzzy.enabled ? config.maps.fuzzy.defaultZoomLevel : 11,
  mapsConfig = config.maps,
  useStaticMap = false,
}) => {
  // Reproduce classNames(rootClassName || css.root, className): the component's own
  // base style applies ONLY when no rootClassName override is passed; className is
  // always appended.
  const rootSxOrNull = rootClassName ? null : rootSx;
  const mapRootSxOrNull = mapRootClassName ? null : mapRootSx;

  // Reactive: mapbox-gl is a deferred script — render the empty placeholder
  // until it has loaded, then flip to the real map.
  // Only the interactive map needs the library; the static map is a plain <img>
  // against the Mapbox Static Images API and authenticates with
  // config.maps.mapboxAccessToken, so it must NOT wait for the deferred script.
  const mapsLibLoaded = useMapsLibLoaded();

  if (mapsConfig.fuzzy.enabled && !obfuscatedCenter) {
    throw new Error(
      'Map: obfuscatedCenter prop is required when config.maps.fuzzy.enabled === true'
    );
  }
  if (!mapsConfig.fuzzy.enabled && !center) {
    throw new Error('Map: center prop is required when config.maps.fuzzy.enabled === false');
  }

  const location = mapsConfig.fuzzy.enabled ? obfuscatedCenter : center;

  if (useStaticMap) {
    return <StaticMap address={address} center={location} mapsConfig={mapsConfig} zoom={zoom} />;
  }

  return !mapsLibLoaded ? (
    <Box className={[rootClassName, className].filter(Boolean).join(' ') || undefined} sx={rootSxOrNull} />
  ) : (
    <DynamicMap
      address={address}
      center={location}
      containerClassName={[rootClassName, className].filter(Boolean).join(' ') || undefined}
      containerSx={rootSxOrNull}
      handleChangeLocation={() => null}
      mapClassName={mapRootClassName || undefined}
      mapSx={mapRootSxOrNull}
      mapsConfig={mapsConfig}
      zoom={zoom}
    />
  );
};

export default memo(MapComponent);
